/**
 * 侧边导航。
 *
 * 用分组（type: 'group'）而不是可折叠子菜单：二级菜单在只有三项时是纯粹的
 * 多余点击，而且展开态还要额外持久化。分组标题只是路标，扫一眼就过。
 */

import { useMemo } from 'react';
import { Menu } from 'antd';
import type { MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ApartmentOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  DeploymentUnitOutlined,
  HistoryOutlined,
  KeyOutlined,
  RobotOutlined,
  TeamOutlined,
} from '@ant-design/icons';

/** 与路由表一一对应。选中态由 pathname 反推，不额外维护状态。 */
const ROUTES = [
  '/dashboard',
  '/workflows',
  '/runs',
  '/settings/model-providers',
  '/settings/credentials',
  '/settings/schedules',
  '/settings/users',
  '/ops',
] as const;

const ITEMS: MenuProps['items'] = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: '概览' },
  { key: '/workflows', icon: <ApartmentOutlined />, label: '工作流' },
  { key: '/runs', icon: <HistoryOutlined />, label: '运行历史' },
  {
    key: 'grp-settings',
    type: 'group',
    label: '配置',
    children: [
      { key: '/settings/model-providers', icon: <RobotOutlined />, label: '模型供应商' },
      { key: '/settings/credentials', icon: <KeyOutlined />, label: '凭据' },
      { key: '/settings/schedules', icon: <ClockCircleOutlined />, label: '定时触发' },
    ],
  },
  {
    key: 'grp-system',
    type: 'group',
    label: '系统',
    children: [
      // 账号归「系统」而不是「配置」：配置组里的三项都是工作流跑起来所需的资源，
      // 账号管的是谁能进这个后台，不是同一类东西。
      { key: '/settings/users', icon: <TeamOutlined />, label: '账号' },
      { key: '/ops', icon: <DeploymentUnitOutlined />, label: '运维' },
    ],
  },
];

export function SideNav({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // /workflows/01J8... 也要让「工作流」保持选中，取最长前缀匹配
  const selectedKey = useMemo(() => {
    const hit = ROUTES.filter((k) => pathname === k || pathname.startsWith(`${k}/`)).sort(
      (a, b) => b.length - a.length,
    )[0];
    return hit ?? '/dashboard';
  }, [pathname]);

  return (
    <Menu
      className="aigzl-nav"
      mode="inline"
      inlineCollapsed={collapsed}
      selectedKeys={[selectedKey]}
      items={ITEMS}
      onClick={({ key }) => navigate(key)}
    />
  );
}
